import type { Maybe } from '@adornis/base/utilTypes.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { createTeaching, subscribeTeachingsForProduct } from '@adornis/buchungsheld-db/api/teaching.js';
import { Teaching } from '@adornis/buchungsheld-db/entities/teaching.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/x-data-view-table.js';
import '@adornis/data-view/client/x-data-view.js';
import '@adornis/popover/x-tooltip.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { firstValueFrom, of, switchMap } from 'rxjs';
import { BhPromptAdminManageLecturerContractOnProduct } from '../prompts/bh-prompt-admin-manage-lecturer-contract-on-product.js';
import { BHPromptUserPicker } from '../prompts/bh-prompt-user-picker.js';

/*
 * @element bh-admin-teaching-overview
 */
@customElement('bh-admin-teaching-overview')
export class BhAdminTeachingOverview extends BuchungsheldLitElement {
  @property({ attribute: 'product-id' }) productID = new RXController<string | null>(this, null);
  @property({ attribute: 'institution-id' }) institutionID?: string;

  @state() private teachings = new RXController(
    this,
    this.productID.observable.pipe(
      switchMap(productID =>
        productID
          ? subscribeTeachingsForProduct(productID)({
              _class: 1,
              lecturer: { firstName: 1, lastName: 1 },
              lecturerID: 1,
            })
          : of([]),
      ),
    ),
  );

  @state() private _newlyCreatedTeachings: Teaching[] = [];
  @state() private _teachingsToBeRemoved: Teaching[] = [];

  public get appliedTeachingList() {
    return this.teachings.value ?? [];
  }

  public get dirtyTeachingList() {
    return (this.teachings.value ?? [])
      .concat(this._newlyCreatedTeachings)
      .filter(teaching => !this._teachingsToBeRemoved.includes(teaching));
  }

  override render() {
    return html`
      <x-flex space="sm">
        <x-flex horizontal crossaxis-center space-between space="md">
          <x-h3>${this._translation.translate('workshop', 'lecturers')}</x-h3>
          <x-button mode="text" icon="add" @click=${async () => this.addLecturer()}>
            ${this._translation.translate('workshop', 'addLecturer')}
          </x-button>
        </x-flex>
        <tr-data-view-table
          .loading=${false}
          .data=${this.dirtyTeachingList}
          .fullOptions=${buildFullOptions({
            class: Teaching,
            customColumns: [
              { fieldName: 'lecturer.firstName' },
              { fieldName: 'lecturer.lastName' },
              {
                customID: '_contract',
                columnWidth: 'max-content',
                renderItem: teaching =>
                  html`
                    <tr-button-small
                      icon="contract_edit"
                      @click=${() =>
                        BhPromptAdminManageLecturerContractOnProduct.showPopup({
                          props: {
                            hasCloseButton: true,
                            teaching,
                            institutionID: this.institutionID,
                          },
                        })}
                    >
                      <x-tooltip
                        .text=${html`
                          <x-text> ${this._translation.translate('lecturer', 'manageContractOnProduct')} </x-text>
                        `}
                      ></x-tooltip>
                    </tr-button-small>
                  `,
              },
              {
                customID: '_options',
                columnWidth: 'max-content',
                renderItem: teaching => html`
                  <tr-button-small
                    icon="delete"
                    tone="error"
                    .color=${this.colors.tone.error}
                    @click=${async () => this.removeLecturer(teaching)}
                  >
                    <x-tooltip
                      .text=${html`
                        <x-text> ${this._translation.translate('lecturer', 'removeFromProduct')} </x-text>
                      `}
                    ></x-tooltip>
                  </tr-button-small>
                `,
              },
            ],
          }).dataViewOptions}
        ></tr-data-view-table>
      </x-flex>
    `;
  }

  private async addLecturer() {
    // TODO What happens if a lecturer already exists but only for a different institution
    // TODO We want to only add the role lecturer with the corresponding context to the user object
    if (!this.institutionID) return TrDialog.alert('fatal: missing institution ID');
    const newLecturer = await BHPromptUserPicker.showPopup<Maybe<User>>({
      props: {
        institutionID: this.institutionID,
        userTypeForNewlyCreatedUser: 'lecturer',
      },
    });
    if (!newLecturer) return;

    // do not add user twice
    if (
      (this.teachings.value ?? [])
        .concat(this._newlyCreatedTeachings)
        .find(teaching => teaching.lecturerID === newLecturer._id)
    ) {
      void XSnackbar.show(this._translation.translate('workshop', 'lecturerAlreadyAdded'));
      return;
    }

    // TODO If not saved yet, no productID exists yet
    const teaching = new Teaching({
      lecturerID: newLecturer._id,
      productID: this.productID.value,
    });

    // optimistic display
    teaching.lecturer = newLecturer;

    this._newlyCreatedTeachings = this._newlyCreatedTeachings.concat([teaching]);

    this.requestUpdate();
  }

  private async removeLecturer(teaching: Teaching) {
    if (this._newlyCreatedTeachings.includes(teaching))
      this._newlyCreatedTeachings = this._newlyCreatedTeachings.filter(t => t !== teaching);
    else this._teachingsToBeRemoved = this._teachingsToBeRemoved.concat([teaching]);
  }

  // need to pass product id because can be called on newly created product
  saveAllTeachings(productID: string) {
    Promise.all([
      ...(this.teachings.value ?? []).map(async teaching => {
        if (!(await firstValueFrom(teaching.dirty))) return;
        return teaching.save();
      }),
      ...this._newlyCreatedTeachings.map(teaching => {
        if (!teaching.productID) teaching.productID = productID;
        return createTeaching(teaching)();
      }),
      ...this._teachingsToBeRemoved.map(async teaching => {
        return teaching.remove();
      }),
    ]);
  }
}
